import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Button, IconButton, InputAdornment, MenuItem, Paper, TextField, Tooltip, Typography
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GroupProductGrid from './GroupProductGrid';
import { TrackingGroupService } from '../TrackingGroupService';
import { formatMaxAlertPrice, parseMaxAlertPrice } from '../utils/maxAlertPrice';

/**
 * "I just want an RTX 5090 under $3,000" (#357). One rule covers every product in a series,
 * including ones added to the catalog later; the first in stock at or under the price is the
 * one the user is emailed about.
 */
const PriceGroups = ({ service = TrackingGroupService, onNotify = () => {} }) => {
    const [seriesOptions, setSeriesOptions] = useState([]);
    const [groups, setGroups] = useState([]);
    const [series, setSeries] = useState('');
    const [priceInput, setPriceInput] = useState('');
    const [busy, setBusy] = useState(false);
    // Groups start open: seeing the products IS the point (#361). Closing one is remembered per visit.
    const [collapsed, setCollapsed] = useState({});
    const [loadError, setLoadError] = useState('');

    const load = useCallback(async () => {
        try {
            const [options, mine] = await Promise.all([service.getSeries(), service.getGroups()]);
            setSeriesOptions(options);
            setGroups(mine);
            setLoadError('');
        } catch (error) {
            setLoadError(error.message || 'Failed to load price groups');
        }
    }, [service]);

    useEffect(() => {
        load();
    }, [load]);

    const parsed = parseMaxAlertPrice(priceInput);
    const selected = seriesOptions.find((option) => option.series === series);
    const existing = groups.find((group) => group.series === series);
    const canSave = Boolean(series) && parsed.value !== null && !parsed.error && !busy;

    const handleSave = async () => {
        if (!canSave) {
            return;
        }
        setBusy(true);
        try {
            await service.saveGroup(series, parsed.value);
            onNotify(`You'll be emailed when any ${series} is in stock at ${formatMaxAlertPrice(parsed.value)} or less`, 'success');
            setSeries('');
            setPriceInput('');
            await load();
        } catch (error) {
            onNotify(error.message || 'Failed to save the price group', 'error');
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async (group) => {
        setBusy(true);
        try {
            await service.deleteGroup(group.id);
            onNotify(`Removed the ${group.series} price group`, 'success');
            await load();
        } catch (error) {
            onNotify(error.message || 'Failed to delete the price group', 'error');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Paper elevation={1} sx={{ p: 2.5, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>Price groups</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Want any card in a series, as long as the price is right? Pick the series and the most you'll pay.
                We'll email you about the first one in stock at that price or less, at any store we track.
            </Typography>

            {loadError && (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>{loadError}</Typography>
            )}

            {groups.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                    {groups.map((group) => (
                        <Box key={group.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Box
                            sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
                                px: 1.5, py: 1
                            }}
                        >
                            <Box>
                                <Typography variant="body1" fontWeight="600">{group.series}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {group.productCount} {group.productCount === 1 ? 'product' : 'products'} · new ones are added automatically
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body1" fontWeight="600" color="primary.main" sx={{ whiteSpace: 'nowrap' }}>
                                    {formatMaxAlertPrice(group.maxPrice)} or less
                                </Typography>
                                <Tooltip title={`Remove the ${group.series} price group`}>
                                    <span>
                                        <IconButton
                                            size="small"
                                            aria-label={`Remove the ${group.series} price group`}
                                            onClick={() => handleDelete(group)}
                                            disabled={busy}
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        </Box>
                        <Box sx={{ px: 1.5, pb: 1.5 }}>
                            <Button size="small" onClick={() => setCollapsed({ ...collapsed, [group.id]: !collapsed[group.id] })} sx={{ mb: 1, px: 0 }}>
                                {collapsed[group.id] ? `Show the ${group.productCount} products` : 'Hide products'}
                            </Button>
                            {!collapsed[group.id] && (
                                <GroupProductGrid series={group.series} maxPrice={group.maxPrice} service={service} />
                            )}
                        </Box>
                        </Box>
                    ))}
                </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                <TextField
                    select
                    label="Series"
                    value={series}
                    onChange={(event) => setSeries(event.target.value)}
                    size="small"
                    disabled={busy}
                    sx={{ minWidth: 190 }}
                >
                    {seriesOptions.map((option) => (
                        <MenuItem key={option.series} value={option.series} disabled={option.productCount === 0}>
                            {option.series} ({option.productCount})
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    label="Maximum price"
                    value={priceInput}
                    onChange={(event) => setPriceInput(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            handleSave();
                        }
                    }}
                    size="small"
                    disabled={busy}
                    error={Boolean(parsed.error)}
                    inputProps={{ inputMode: 'decimal', 'aria-label': 'Maximum price for the group' }}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    sx={{ width: 170 }}
                />
                <Button variant="contained" onClick={handleSave} disabled={!canSave} sx={{ mt: '2px' }}>
                    {existing ? 'Update group' : 'Add group'}
                </Button>
            </Box>
            <Typography variant="caption" color={parsed.error ? 'error' : 'text.secondary'} sx={{ display: 'block', mt: 1 }}>
                {parsed.error
                    || (selected
                        ? `${selected.productCount} ${selected.series} ${selected.productCount === 1 ? 'product' : 'products'} tracked`
                          + (selected.lowestPrice ? `, from ${formatMaxAlertPrice(selected.lowestPrice)} at Best Buy.` : '.')
                          + (existing ? ` You already have this group at ${formatMaxAlertPrice(existing.maxPrice)}; saving changes the price.` : '')
                        : 'Alerts go to your email, for online stock and for pickup in the states you chose.')}
            </Typography>

            {selected && !existing && (
                <Box sx={{ mt: 2 }}>
                    <GroupProductGrid series={series} maxPrice={parsed.error ? null : parsed.value} service={service} />
                </Box>
            )}
        </Paper>
    );
};

export default PriceGroups;
